import React, { useState, useEffect, useRef, useCallback } from "react";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { DEVELOPERS } from "../constants/developers";
import { PARTNER_LOGOS } from "../constants/logos";
import LeadCaptureModal from "../components/common/LeadCaptureModal";
import DevelopersLogoCarousel from "../components/home/DevelopersLogoCarousel";
import { LEAD_CHANNELS, CONTACT_WHATSAPP } from "../constants/data";
import { builderFolderImages } from "../services/api";

const GOLD = "#50C878";

// ─── Single builder's hero carousel ───────────────────────────
function DevGallery({ images, alt }) {
  const [idx, setIdx] = useState(0);
  const total = images.length;
  const next = useCallback(() => setIdx((i) => (i + 1) % total), [total]);
  const prev = useCallback(() => setIdx((i) => (i - 1 + total) % total), [total]);

  // Reset to first slide whenever the image set changes (e.g. stock → Supabase).
  useEffect(() => {
    setIdx(0);
  }, [total]);

  useEffect(() => {
    if (total <= 1) return;
    const t = setInterval(next, 4500);
    return () => clearInterval(t);
  }, [next, total]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "340px",
        borderRadius: "14px",
        overflow: "hidden",
        border: "1px solid rgba(80,200,120,0.2)",
      }}
    >
      <img
        src={images[idx]}
        alt={`${alt} ${idx + 1}`}
        style={{ width: "100%", height: "100%", objectFit: "cover", transition: "opacity 0.4s" }}
      />
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to bottom, transparent 55%, rgba(0,0,0,0.55) 100%)",
        }}
      />
      {total > 1 && (
        <>
          <button onClick={prev} aria-label="Previous image" style={{ ...galleryArrow, left: "12px" }}>
            <ChevronLeft size={18} />
          </button>
          <button onClick={next} aria-label="Next image" style={{ ...galleryArrow, right: "12px" }}>
            <ChevronRight size={18} />
          </button>
        </>
      )}

      {/* Dots */}
      {total > 1 && (
        <div
          style={{
            position: "absolute",
            bottom: "12px",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: "6px",
            zIndex: 3,
          }}
        >
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              aria-label={`Image ${i + 1}`}
              style={{
                width: i === idx ? "20px" : "7px",
                height: "7px",
                borderRadius: "4px",
                background: i === idx ? GOLD : "rgba(1,50,32,0.62)",
                border: "none",
                cursor: "pointer",
                transition: "all 0.3s",
                padding: 0,
              }}
            />
          ))}
        </div>
      )}

      {/* Counter */}
      <span
        style={{
          position: "absolute",
          top: "12px",
          right: "14px",
          background: "rgba(0,0,0,0.5)",
          color: "#013220",
          fontFamily: "'Inter',sans-serif",
          fontSize: "0.72rem",
          padding: "3px 9px",
          borderRadius: "12px",
          zIndex: 3,
        }}
      >
        {idx + 1} / {total}
      </span>
    </div>
  );
}

// ─── Single builder block ─────────────────────────────────────
function DeveloperSection({ dev, onInterested, registerRef }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Hero carousel images: prefer this builder's Supabase folder images;
  // fall back to the stock gallery while loading or if the folder is empty.
  const [gallery, setGallery] = useState(dev.gallery);

  useEffect(() => {
    registerRef(dev.id, ref.current);
  }, [dev.id, registerRef]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const urls = await builderFolderImages(dev.name);
        if (alive && urls && urls.length) setGallery(urls);
      } catch (_) {
        /* keep stock fallback */
      }
    })();
    return () => {
      alive = false;
    };
  }, [dev.name]);

  return (
    <section
      ref={ref}
      id={dev.id}
      style={{
        scrollMarginTop: "88px",
        background: "linear-gradient(135deg, #FFFFFF 0%, #F7FBF8 100%)",
        border: "1px solid rgba(80,200,120,0.15)",
        borderRadius: "16px",
        padding: "28px",
        marginBottom: "32px",
      }}
    >
      {/* Header: logo + name */}
      <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "22px", flexWrap: "wrap" }}>
        <div
          style={{
            background: "rgba(80,200,120,0.10)",
            border: "1px solid rgba(80,200,120,0.25)",
            borderRadius: "12px",
            padding: "10px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "120px",
            height: "70px",
          }}
        >
          <img
            src={PARTNER_LOGOS[dev.logoIndex - 1]}
            alt={dev.name}
            style={{ maxWidth: "130px", maxHeight: "52px", objectFit: "contain" }}
          />
        </div>
        <div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.8rem", fontWeight: 600, color: "#013220", margin: 0 }}>
            {dev.name}
          </h2>
          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.14em", textTransform: "uppercase" }}>
            Builder Partner
          </span>
        </div>
      </div>

      {/* Hero carousel (Supabase folder images, stock fallback) */}
      <DevGallery images={gallery} alt={dev.name} />

      {/* I'm Interested button */}
      <div style={{ display: "flex", justifyContent: "center", margin: "22px 0 18px" }}>
        <button
          onClick={() => onInterested(dev)}
          style={{
            background: "linear-gradient(135deg, #50C878, #7FD9A0)",
            color: "#FFFFFF",
            border: "none",
            padding: "13px 44px",
            borderRadius: "6px",
            fontFamily: "'Inter', sans-serif",
            fontWeight: 700,
            fontSize: "0.95rem",
            cursor: "pointer",
            letterSpacing: "0.04em",
            transition: "transform 0.2s, box-shadow 0.2s",
          }}
          onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(80,200,120,0.45)"; }}
          onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
        >
          I'm Interested
        </button>
      </div>

      {/* 2-line paragraph */}
      <p
        style={{
          fontFamily: "'Inter', sans-serif",
          fontSize: "0.95rem",
          color: "rgba(1,50,32,0.85)",
          lineHeight: 1.7,
          textAlign: "center",
          maxWidth: "760px",
          margin: "0 auto 16px",
        }}
      >
        {dev.tagline}
      </p>

      {/* More Details toggle */}
      <div style={{ textAlign: "center" }}>
        <button
          onClick={() => setOpen((o) => !o)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "6px",
            background: "transparent",
            border: `1px solid ${GOLD}`,
            borderRadius: "4px",
            padding: "9px 22px",
            color: GOLD,
            fontFamily: "'Inter', sans-serif",
            fontWeight: 600,
            fontSize: "0.85rem",
            cursor: "pointer",
            letterSpacing: "0.04em",
            transition: "background 0.2s",
          }}
          onMouseOver={(e) => (e.currentTarget.style.background = "rgba(80,200,120,0.1)")}
          onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
        >
          {open ? "Less Details" : "More Details"}
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
      </div>

      {open && (
        <div
          style={{
            marginTop: "20px",
            padding: "20px 24px",
            background: "rgba(80,200,120,0.05)",
            border: "1px solid rgba(80,200,120,0.12)",
            borderRadius: "10px",
          }}
        >
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.92rem", color: "rgba(1,50,32,0.85)", lineHeight: 1.8, margin: 0 }}>
            {dev.description}
          </p>
        </div>
      )}
    </section>
  );
}

const galleryArrow = {
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  zIndex: 3,
  width: "38px",
  height: "38px",
  borderRadius: "50%",
  background: "rgba(80,200,120,0.10)",
  border: "1px solid rgba(1,50,32,0.48)",
  color: "#013220",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  backdropFilter: "blur(4px)",
};

export default function DevelopersPage() {
  const [modalDev, setModalDev] = useState(null);
  const refs = useRef({});

  const registerRef = useCallback((id, node) => {
    if (node) refs.current[id] = node;
  }, []);

  // Scroll to a builder when arriving via /developers#<id>
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    if (!hash) return;
    // wait for sections to mount
    const t = setTimeout(() => {
      const el = refs.current[hash] || document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 300);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{ background: "#F2F8F3", color: "#013220", minHeight: "100vh", paddingTop: "68px" }}>
      {/* Page header */}
      <div
        style={{
          padding: "56px 24px 36px",
          textAlign: "center",
          background: "linear-gradient(135deg, rgba(80,200,120,0.10), rgba(127,217,160,0.04))",
          borderBottom: "1px solid rgba(80,200,120,0.2)",
        }}
      >
        <span style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", color: GOLD, letterSpacing: "0.18em", textTransform: "uppercase" }}>
          Our Builder Network
        </span>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 600, color: "#013220", margin: "12px 0 10px" }}>
          Our Developers
        </h1>

        {/* NEW LINE ADDED HERE */}
        <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.95rem", color: "rgba(1,50,32,0.62)", maxWidth: "640px", margin: "12px auto 0" }}>
          GroupBuy Flats partners with developers to accelerate cash flow and clear inventory rapidly through bulk sales, adding immediate financial velocity to current and upcoming projects.
        </p>

        {/* 3-row continuously-scrolling logo carousel (equally divided) */}
        <div style={{ maxWidth: "1180px", margin: "34px auto 30px" }}>
          <DevelopersLogoCarousel />
        </div>

        {/* Hero CTAs */}
        <div style={{ display: "flex", gap: "14px", justifyContent: "center", flexWrap: "wrap" }}>
          <button
            onClick={() => setModalDev({ name: "our builder network" })}
            style={{
              background: "linear-gradient(135deg, #50C878, #7FD9A0)",
              color: "#FFFFFF",
              border: "none",
              padding: "13px 34px",
              borderRadius: "6px",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 700,
              fontSize: "0.92rem",
              cursor: "pointer",
              letterSpacing: "0.04em",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}
            onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 8px 24px rgba(80,200,120,0.45)"; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "none"; }}
          >
            Register Your Interest
          </button>
          <a
            href={CONTACT_WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: "transparent",
              color: "#013220",
              border: "1px solid rgba(1,50,32,0.35)",
              padding: "13px 34px",
              borderRadius: "6px",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 600,
              fontSize: "0.92rem",
              textDecoration: "none",
              letterSpacing: "0.04em",
            }}
          >
            Talk to an Advisor
          </a>
        </div>
      </div>

      {/* All builders */}
      <div style={{ maxWidth: "1100px", margin: "0 auto", padding: "40px 24px 80px" }}>
        {DEVELOPERS.map((dev) => (
          <DeveloperSection
            key={dev.id}
            dev={dev}
            onInterested={setModalDev}
            registerRef={registerRef}
          />
        ))}
      </div>

      {/* Lead modal — prefilled with builder context */}
      <LeadCaptureModal
        isOpen={!!modalDev}
        onClose={() => setModalDev(null)}
        channel={LEAD_CHANNELS.WEB}
        prefillMessage={modalDev ? `Interested in projects by ${modalDev.name}` : ""}
      />
    </div>
  );
}